import React, { useCallback, useEffect, useState } from 'react';
import EvaluacionesConfirm from './EvaluacionesConfirm';
import { formatFechaHora, formatPromedio, toDateTimeLocalValue } from '../../utils/evaluaciones';
import './Evaluaciones.css';

// Instancias de evaluación de un curso (o de una materia del curso, en colegios): alta, edición, baja y acceso a la planilla.

const EMPTY_FORM = { id: '', nombre: '', descripcion: '', fechaHora: '' };

const defaultFechaHora = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:00`;
};

const EvaluacionesLista = ({ api, idCursoMateria, onCalificar, onChanged }) => {
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEvaluaciones(await api.listar(idCursoMateria));
      setError('');
    } catch (err) {
      setError(err?.message || 'No pudimos cargar las evaluaciones.');
    } finally {
      setLoading(false);
    }
  }, [api, idCursoMateria]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm({ ...EMPTY_FORM, fechaHora: defaultFechaHora() });
    setFormError('');
  };

  const openEdit = (evaluacion) => {
    setForm({
      id: evaluacion.id,
      nombre: evaluacion.nombre,
      descripcion: evaluacion.descripcion,
      fechaHora: toDateTimeLocalValue(evaluacion.fechaHora)
    });
    setFormError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.nombre.trim()) {
      setFormError('Poné un nombre a la evaluación.');
      return;
    }
    if (!form.fechaHora) {
      setFormError('Elegí la fecha y la hora.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const data = { nombre: form.nombre.trim(), descripcion: form.descripcion.trim(), fechaHora: form.fechaHora };
      if (form.id) {
        await api.actualizar(form.id, data);
      } else {
        await api.crear(data, idCursoMateria);
      }
      setForm(null);
      await load();
      onChanged?.();
    } catch (err) {
      setFormError(err?.message || 'No pudimos guardar la evaluación.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.eliminar(deleteTarget.id);
      setDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.message || 'No pudimos eliminar la evaluación.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="ev-lista">
      <div className="ev-toolbar">
        <span className="ev-toolbar-info">
          {loading ? 'Cargando...' : `${evaluaciones.length} ${evaluaciones.length === 1 ? 'instancia' : 'instancias'} de evaluación`}
        </span>
        {!form && (
          <button type="button" className="ev-btn ev-btn-primary" onClick={openNew}>
            <i className="fas fa-plus" aria-hidden="true" />
            Nueva evaluación
          </button>
        )}
      </div>

      {form && (
        <form className="ev-form" onSubmit={handleSubmit}>
          <h4>{form.id ? 'Editar evaluación' : 'Nueva evaluación'}</h4>
          <div className="ev-form-grid">
            <label>
              Nombre
              <input
                type="text"
                value={form.nombre}
                onChange={(event) => setForm((prev) => ({ ...prev, nombre: event.target.value }))}
                placeholder="Ej.: Parcial 1, TP integrador, Oral"
                maxLength={150}
                autoFocus
                disabled={saving}
              />
            </label>
            <label>
              Fecha y hora
              <input
                type="datetime-local"
                value={form.fechaHora}
                onChange={(event) => setForm((prev) => ({ ...prev, fechaHora: event.target.value }))}
                disabled={saving}
              />
            </label>
            <label className="ev-form-wide">
              Descripción <small>(opcional)</small>
              <textarea
                value={form.descripcion}
                onChange={(event) => setForm((prev) => ({ ...prev, descripcion: event.target.value }))}
                placeholder="Temas, modalidad, consignas..."
                maxLength={1000}
                rows={2}
                disabled={saving}
              />
            </label>
          </div>
          {formError && <p className="ev-error-text" role="alert">{formError}</p>}
          <div className="ev-form-actions">
            <button type="button" className="ev-btn ev-btn-secondary" onClick={() => setForm(null)} disabled={saving}>Cancelar</button>
            <button type="submit" className="ev-btn ev-btn-primary" disabled={saving}>
              <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} aria-hidden="true" />
              {form.id ? 'Guardar cambios' : 'Crear evaluación'}
            </button>
          </div>
        </form>
      )}

      {error && <p className="ev-error-text" role="alert">{error}</p>}

      {!loading && evaluaciones.length === 0 && !form ? (
        <div className="ev-empty">
          <i className="fas fa-clipboard-list" aria-hidden="true" />
          <p>Todavía no hay evaluaciones. Creá la primera para empezar a cargar notas.</p>
        </div>
      ) : (
        <div className="ev-table-wrap">
          <table className="ev-table">
            <thead>
              <tr>
                <th>Evaluación</th>
                <th>Fecha y hora</th>
                <th>Calificados</th>
                <th>Promedio</th>
                <th className="ev-actions-col">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {evaluaciones.map((evaluacion) => (
                <tr key={evaluacion.id}>
                  <td>
                    <strong>{evaluacion.nombre}</strong>
                    {evaluacion.descripcion && <small className="ev-cell-sub">{evaluacion.descripcion}</small>}
                  </td>
                  <td>{formatFechaHora(evaluacion.fechaHora)}</td>
                  <td>
                    <span className="ev-progress" title={`${evaluacion.cantidadAusentes} ausentes`}>
                      <span style={{ width: `${evaluacion.totalAlumnos ? Math.round(((evaluacion.cantidadCalificados + evaluacion.cantidadAusentes) * 100) / evaluacion.totalAlumnos) : 0}%` }} />
                    </span>
                    {evaluacion.cantidadCalificados}/{evaluacion.totalAlumnos}
                    {evaluacion.cantidadAusentes > 0 && (
                      <small className="ev-cell-sub">{evaluacion.cantidadAusentes} {evaluacion.cantidadAusentes === 1 ? 'ausente' : 'ausentes'}</small>
                    )}
                  </td>
                  <td>{formatPromedio(evaluacion.promedio)}</td>
                  <td className="ev-actions-col">
                    <button type="button" className="ev-btn ev-btn-primary ev-btn-sm" onClick={() => onCalificar(evaluacion)}>
                      <i className="fas fa-pen-nib" aria-hidden="true" />
                      Calificar
                    </button>
                    <button type="button" className="ev-icon-btn" onClick={() => openEdit(evaluacion)} title="Editar">
                      <i className="fas fa-pen" aria-hidden="true" />
                    </button>
                    <button type="button" className="ev-icon-btn danger" onClick={() => setDeleteTarget(evaluacion)} title="Eliminar">
                      <i className="fas fa-trash" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget && (
        <EvaluacionesConfirm
          title="Eliminar evaluación"
          confirmLabel="Eliminar"
          danger
          busy={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        >
          ¿Eliminar <strong>{deleteTarget.nombre}</strong>? Sus notas dejan de contar para el estado académico de los alumnos.
        </EvaluacionesConfirm>
      )}
    </div>
  );
};

export default EvaluacionesLista;
